import {DateRange} from '@mui/lab';
import moment from 'moment';
import {TimeLapseType} from '../../enums/time-lapse-type';

/**
 * Returns the date range for a specific time lapse type.
 *
 * Note: The custom time lapse is not included here since this is user specific.
 */
export const getTimeLapseRange = (
  type: Exclude<TimeLapseType, TimeLapseType.Custom>
): DateRange<Date> => {
  // Get now
  const now = moment();

  switch (type) {
    case TimeLapseType.Last180Days:
      // Last 180 days start
      const last180DaysStart_last180Days = now.clone().subtract(179, 'days').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Last 180 days end: we need to disable the future so we use the current day
      const last180DaysEnd_last180Days = now.clone();

      // Return
      return [last180DaysStart_last180Days.toDate(), last180DaysEnd_last180Days.toDate()];
    case TimeLapseType.Last30Days:
      // Last 30 days start
      const last30DaysStart_last30Days = now.clone().subtract(29, 'days').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Last 30 days end: we need to disable the future so we use the current day
      const last30DaysEnd_last30Days = now.clone();

      // Return
      return [last30DaysStart_last30Days.toDate(), last30DaysEnd_last30Days.toDate()];
    case TimeLapseType.Last365Days:
      // Last 365 days start
      const last365DaysStart_last365Days = now.clone().subtract(364, 'days').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Last 365 days end: we need to disable the future so we use the current day
      const last365DaysEnd_last365Days = now.clone();

      // Return
      return [last365DaysStart_last365Days.toDate(), last365DaysEnd_last365Days.toDate()];
    case TimeLapseType.LastMonth:
      // Last month start
      const monthStart_lastMonth = now.clone().startOf('month').subtract(1, 'months').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Last month end
      // Note: endOf was giving the wrong date
      const monthEnd_lastMonth = monthStart_lastMonth
        .clone()
        .add(1, 'months')
        .subtract(1, 'days')
        .set({
          hours: 23,
          minutes: 59,
          seconds: 59,
          milliseconds: 999,
        });

      // Return
      return [monthStart_lastMonth.toDate(), monthEnd_lastMonth.toDate()];
    case TimeLapseType.LastWeek:
      // End last week
      const thisWeekStart_lastWeek = now.clone().startOf('week');
      const weekEnd_lastWeek = thisWeekStart_lastWeek.clone().subtract(1, 'days').set({
        hours: 23,
        minutes: 59,
        seconds: 59,
        milliseconds: 999,
      });

      // Start last week
      const weekStart_lastWeek = weekEnd_lastWeek.clone().startOf('week').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Return
      return [weekStart_lastWeek.toDate(), weekEnd_lastWeek.toDate()];
    case TimeLapseType.ThisMonth:
      // Month start
      const monthStart_thisMonth = now.clone().startOf('month').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Month end: we need to disable the future so we use the current day
      const monthEnd_thisMonth = now.clone();

      // Return
      return [monthStart_thisMonth.toDate(), monthEnd_thisMonth.toDate()];
    case TimeLapseType.ThisWeek:
      // Week start
      const weekStart_thisWeek = now.clone().startOf('week').set({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Week end: we need to disable the future so we use the current day
      const weekEnd_thisWeek = now.clone();

      // Return
      return [weekStart_thisWeek.toDate(), weekEnd_thisWeek.toDate()];
  }
};
